import { useState, useEffect, useCallback, useRef } from 'react';
import './BrokerRatingWidget.css';
import {
  CompanyRecommendationType,
  TargetPriceType,
} from '../../types/company.type';
import { ReactComponent as TeamIcon } from '../../assets/icons/BrokerRating/team-line.svg';
import { ReactComponent as BrokerRatingInfoIcon } from '../../assets/icons/tooltip-company-score-icon.svg';
import BrokerTable, {
  BrokerItem,
} from '../../Components/BrokerTable/BrokerTable';
import BrokerRatingChart from '../../Components/BrokerRatingChart/BrokerRatingChartV2';
import Layout from '../../Components/Common/Layout/Layout';
import InfoPanel from '../../Components/Common/InfoPanel/InfoPanel';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';
import {
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';

import { getBrokerRating } from '../../api/company.api';
import useResizeNotifier from '../../hooks/useResizeNotifier';

export interface BrokerRatingWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const BROKER_RATING_INFO_TITLE = 'About Analyst Recommendations';

const BROKER_RATING_INFO_CONTENT = (
  <div className="brokerRating-info-text">
    <p>
      Powered by LSEG I/B/E/S, which aggregates research from 18,000+ analysts
      across 950+ research providers, covering 22,000+ companies globally.
    </p>
    <p>
      The displayed recommendation represents the consensus of analysts covering
      this company. The Analysts count indicates the number of analyst
      recommendations contributing to the current consensus.
    </p>
  </div>
);

interface CompanyData {
  date_type: string;
  ric: string;
  ticker: string;
  bell_code: string;
  name: string;
  mean_label: string;
  mean_rating: number;
  numofbrokers: number;
  ratinglist: { key: string; value: number }[];
}

const BrokerRatingWidget = (props: BrokerRatingWidgetProps) => {
  const { onHeightChange } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  useResizeNotifier(
    containerRef,
    WidgetNameSource.BrokerRating,
    onHeightChange,
  );

  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [error, setError] = useState<string | null>(null);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  useEffect(() => {
    if (!isInfoOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsInfoOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isInfoOpen]);
  const msgItem = errorMsg.find((obj) => obj.key === 'brokerRating');
  // const [data, setData] = useState<SectorPeerComparisonDataType[]>([]);

  const [ticker, setTicker] = useState(null);
  const [rcmdlabel, setRcmdlabel] = useState(null);
  const [rcmdlabelClass, setRcmdlabelClass] = useState('');
  const [numofbrokers, setNumofbrokers] = useState('');
  const [maxNumberOfBrokers, setMaxNumberOfBrokers] = useState(0);
  const RECOMMENDATION_MAPPING = [
    { key: 5, min: 4.5, max: 5, label: 'Sell', class: 'sell' },
    { key: 4, min: 3.5, max: 4.5, label: 'Reduce', class: 'reduce' },
    { key: 3, min: 2.5, max: 3.5, label: 'Hold', class: 'hold' },
    { key: 2, min: 1.5, max: 2.5, label: 'Buy', class: 'buy' },
    { key: 1, min: 0, max: 1.5, label: 'Strong Buy', class: 'strongBuy' },
  ];

  const [brokerTable, setBrokerTable] = useState<BrokerItem[]>([
    {
      title: 'Strong Buy',
      key: 'strongBuy',
      accessor: 'noOfBuyRatings',
      totalAnalysist: 0,
    },
    {
      title: 'Buy',
      key: 'buy',
      accessor: 'noOfOutperformRatings',
      totalAnalysist: 0,
    },
    {
      title: 'Hold',
      key: 'hold',
      accessor: 'noOfHoldRatings',
      totalAnalysist: 0,
    },
    {
      title: 'Reduce',
      key: 'reduce',
      accessor: 'noOfUnderperformRatings',
      totalAnalysist: 0,
    },
    {
      title: 'Sell',
      key: 'sell',
      accessor: 'noOfSellRatings',
      totalAnalysist: 0,
    },
  ]);
  const [companyRecommandation, setCompanyRecommandation] =
    useState<CompanyRecommendationType>({
      '12MA': {
        mean: 0,
        strongBuy: 0,
        buy: 0,
        hold: 0,
        reduce: 0,
        sell: 0,
      },
      '2MA': {
        mean: 0,
        strongBuy: 0,
        buy: 0,
        hold: 0,
        reduce: 0,
        sell: 0,
      },
      '1MA': {
        mean: 0,
        strongBuy: 0,
        buy: 0,
        hold: 0,
        reduce: 0,
        sell: 0,
      },
      CURR: {
        mean: 0,
        strongBuy: 0,
        buy: 0,
        hold: 0,
        reduce: 0,
        sell: 0,
      },
    });

  // Call API when page changes
  useEffect(() => {
    fetchRecommendationData();
  }, [props.token, props.symbol]);

  const fetchRecommendationData = useCallback(async () => {
    setIsLoading(true);

    setError(null);
    setIsEmptyData(false);

    try {
      const res = await getBrokerRating(props.symbol || '', props.token || '');

      if (res.data.length === 0) {
        setIsEmptyData(true);
        props.onNoData?.({
          type: WidgetMessageType.NoData,
          source: 'BrokerRatingWidget',
          data: null,
        });
        return;
      }

      // Find the entry with date_type = "CURR"
      const curr = res.data.find(
        (item: CompanyData) => item.date_type === 'CURR',
      );
      if (curr) {
        setTicker(curr.ticker);
        setNumofbrokers(curr.numofbrokers);

        setRcmdlabel(curr.mean_label);
        const meanLabelClass = RECOMMENDATION_MAPPING
          ? RECOMMENDATION_MAPPING.find(
              (mapping) =>
                curr.mean_rating > mapping.min &&
                curr.mean_rating <= mapping.max,
            )
          : undefined;
        setRcmdlabelClass(meanLabelClass ? meanLabelClass.class : '');

        const strongBuyValue = curr.ratinglist.find(
          (r: { key: string; value: number }) => r.key === 'Strong Buy',
        )?.value;
        const buyValue = curr.ratinglist.find(
          (r: { key: string; value: number }) => r.key === 'Buy',
        )?.value;
        const holdValue = curr.ratinglist.find(
          (r: { key: string; value: number }) => r.key === 'Hold',
        )?.value;
        const reduceValue = curr.ratinglist.find(
          (r: { key: string; value: number }) => r.key === 'Reduce',
        )?.value;
        const sellValue = curr.ratinglist.find(
          (r: { key: string; value: number }) => r.key === 'Sell',
        )?.value;

        setBrokerTable((prev) =>
          prev.map((item) => {
            if (item.key === 'strongBuy') {
              return {
                ...item,
                key: 'strongBuy',
                totalAnalysist: strongBuyValue,
              };
            }
            if (item.key === 'buy') {
              return { ...item, key: 'buy', totalAnalysist: buyValue };
            }
            if (item.key === 'hold') {
              return { ...item, key: 'hold', totalAnalysist: holdValue };
            }
            if (item.key === 'reduce') {
              return { ...item, key: 'reduce', totalAnalysist: reduceValue };
            }
            if (item.key === 'sell') {
              return { ...item, key: 'sell', totalAnalysist: sellValue };
            }
            return item;
          }),
        );
      }

      // find max of number of brokers
      const max = Math.max(
        ...res.data.map((item: CompanyData) => item.numofbrokers),
      );
      setMaxNumberOfBrokers(max);
      // Bar chart
      res.data.map((item: CompanyData) => {
        const keys = item.ratinglist.map((r) => r.key);
        const strongBuyEntry = item.ratinglist.find(
          (item) => item.key === 'Strong Buy',
        );
        const buyEntry = item.ratinglist.find((item) => item.key === 'Buy');
        const holdEntry = item.ratinglist.find((item) => item.key === 'Hold');
        const reduceEntry = item.ratinglist.find(
          (item) => item.key === 'Reduce',
        );
        const sellEntry = item.ratinglist.find((item) => item.key === 'Sell');

        const values = [
          strongBuyEntry?.value ?? 0,
          buyEntry?.value ?? 0,
          holdEntry?.value ?? 0,
          reduceEntry?.value ?? 0,
          sellEntry?.value ?? 0,
        ];
        const maxValue = Math.max(...values);

        if (item.date_type === '12MA') {
          setCompanyRecommandation((prev) => ({
            ...prev,
            '12MA': {
              ...prev['12MA'],
              mean: item.mean_rating ? item.mean_rating : 0,
              strongBuy: strongBuyEntry ? strongBuyEntry.value : 0,
              buy: buyEntry ? buyEntry.value : 0,
              hold: holdEntry ? holdEntry.value : 0,
              reduce: reduceEntry ? reduceEntry.value : 0,
              sell: sellEntry ? sellEntry.value : 0,
            },
          }));
        }

        if (item.date_type === '2MA') {
          setCompanyRecommandation((prev) => ({
            ...prev,
            '2MA': {
              ...prev['2MA'],
              mean: item.mean_rating ? item.mean_rating : 0,
              strongBuy: strongBuyEntry ? strongBuyEntry.value : 0,
              buy: buyEntry ? buyEntry.value : 0,
              hold: holdEntry ? holdEntry.value : 0,
              reduce: reduceEntry ? reduceEntry.value : 0,
              sell: sellEntry ? sellEntry.value : 0,
            },
          }));
        }

        if (item.date_type === '1MA') {
          setCompanyRecommandation((prev) => ({
            ...prev,
            '1MA': {
              ...prev['1MA'],
              mean: item.mean_rating ? item.mean_rating : 0,
              strongBuy: strongBuyEntry ? strongBuyEntry.value : 0,
              buy: buyEntry ? buyEntry.value : 0,
              hold: holdEntry ? holdEntry.value : 0,
              reduce: reduceEntry ? reduceEntry.value : 0,
              sell: sellEntry ? sellEntry.value : 0,
            },
          }));
        }

        if (item.date_type === 'CURR') {
          setCompanyRecommandation((prev) => ({
            ...prev,
            CURR: {
              ...prev['CURR'],
              mean: item.mean_rating ? item.mean_rating : 0,
              strongBuy: strongBuyEntry ? strongBuyEntry.value : 0,
              buy: buyEntry ? buyEntry.value : 0,
              hold: holdEntry ? holdEntry.value : 0,
              reduce: reduceEntry ? reduceEntry.value : 0,
              sell: sellEntry ? sellEntry.value : 0,
            },
          }));
        }
      });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch recommendation data');
      console.error('Error fetching recommendation data:', err);
      props.onError?.({
        message: err.message,
        type: err.name + '_' + err.code,
      });
      setIsEmptyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [props.token, props.symbol]);

  return (
    <Layout theme={props?.theme || ThemeType.Direct}>
      <div className="brokerRating-container" ref={containerRef}>
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="brokerRating">
            {isInfoOpen && (
              <InfoPanel
                title={BROKER_RATING_INFO_TITLE}
                onClose={() => setIsInfoOpen(false)}
              >
                {BROKER_RATING_INFO_CONTENT}
              </InfoPanel>
            )}
            <div className="brokerRating-card-container">
              <div className="brokerRating-card-header">
                <div
                  className={`brokerRating-card-recommendation-container ${rcmdlabelClass}`}
                >
                  <span
                    className={`brokerRating-card-recommendation`}
                  >{`${rcmdlabel ? rcmdlabel : '-'}`}</span>
                </div>
              </div>
              <div className="brokerRating-card-content">
                <div className="brokerRating-card-potential-row">
                  <div className="brokerRating-card-potential">
                    <TeamIcon className="brokerRating-card-icon" />
                    &nbsp;{`${numofbrokers} Analysts`}
                  </div>
                  <button
                    type="button"
                    className="brokerRating-info-button"
                    aria-label={BROKER_RATING_INFO_TITLE}
                    onClick={() => setIsInfoOpen(true)}
                  >
                    <BrokerRatingInfoIcon />
                  </button>
                </div>

                <div className="brokerRating-card-description">
                  {`Using ratings from ${numofbrokers} analysts, the average recommendation for ${ticker} on a standardized 5-point scale was determined.`}
                </div>
              </div>
            </div>
            <div className="brokerRating-chart-group">
              <div className="brokerRating-table-wrapper">
                <BrokerTable data={brokerTable} />
              </div>
              <div className="brokerRating-chart-wrapper">
                <BrokerRatingChart
                  companyRecommandation={companyRecommandation}
                  maxNumberOfBrokers={maxNumberOfBrokers}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BrokerRatingWidget;
